import { readExtractionSuggestion } from '@/features/inventorization/domain/review/inventorization-extraction-suggestions';
import {
  INVENTORIZE_REVIEW_STATUSES,
  type InventorizeReviewStatus,
} from '@/features/inventorization/domain/review/inventorization-review-status';
import type { InventorizeReviewSourceKind } from '@/features/inventorization/domain/review/inventorize-review-field-types';
import type { AiExtraction } from '@/shared/capture/capture-session';
import type { PartialInventorizationExtraction } from '@/shared/ocr/inventorize-extraction-contract';
import { type OcrCandidate, type OcrCandidateField, readOcrCandidates } from '@/shared/ocr/ocr-candidates';

export type ResolvedInventorizationValueStatus = Exclude<
  InventorizeReviewStatus,
  typeof INVENTORIZE_REVIEW_STATUSES.waived
>;

export interface ResolvedInventorizationValue {
  id: string;
  status: ResolvedInventorizationValueStatus;
  value: string;
  confidence?: number;
  source?: string;
  sourceKind?: InventorizeReviewSourceKind;
}

export interface InventorizationValueInput {
  aiExtractions: AiExtraction[];
  allowedValues?: readonly string[];
  candidateField?: OcrCandidateField;
  extraction?: PartialInventorizationExtraction;
  extractionPath?: string;
  id: string;
  source?: string;
  value: string;
}

const PARSED_CONFIDENCE_THRESHOLD = 0.75;

export function resolveInventorizationTextValue(input: InventorizationValueInput): ResolvedInventorizationValue {
  const normalizedValue = input.value.trim();
  const candidate = input.candidateField ? readBestCandidate(input.aiExtractions, input.candidateField) : undefined;
  const extractionSuggestion =
    input.extraction && input.extractionPath
      ? readExtractionSuggestion(input.extraction, input.extractionPath, input.allowedValues)
      : undefined;

  if (normalizedValue) {
    return {
      id: input.id,
      source: input.source,
      status: INVENTORIZE_REVIEW_STATUSES.manual,
      value: normalizedValue,
    };
  }

  if (extractionSuggestion?.value) {
    const confidence = extractionSuggestion.confidence ?? 0.5;

    return {
      confidence,
      id: input.id,
      source: input.source,
      sourceKind: 'ai',
      status:
        confidence >= PARSED_CONFIDENCE_THRESHOLD
          ? INVENTORIZE_REVIEW_STATUSES.parsed
          : INVENTORIZE_REVIEW_STATUSES.unsure,
      value: extractionSuggestion.value,
    };
  }

  if (candidate && isAllowedValue(candidate.normalizedValue ?? candidate.value, input.allowedValues)) {
    return {
      confidence: candidate.confidence,
      id: input.id,
      source: candidate.sourcePhotoId.slice(0, 8),
      sourceKind: 'photo',
      status:
        candidate.confidence >= PARSED_CONFIDENCE_THRESHOLD
          ? INVENTORIZE_REVIEW_STATUSES.parsed
          : INVENTORIZE_REVIEW_STATUSES.unsure,
      value: candidate.normalizedValue ?? candidate.value,
    };
  }

  return {
    id: input.id,
    status: INVENTORIZE_REVIEW_STATUSES.empty,
    value: '',
  };
}

function isAllowedValue(value: string, allowedValues: readonly string[] | undefined) {
  return allowedValues === undefined || allowedValues.includes(value);
}

export function isEffectiveResolvedInventorizationValue(
  value: ResolvedInventorizationValue | undefined,
): value is ResolvedInventorizationValue {
  if (!value?.value) {
    return false;
  }

  return (
    value.status === INVENTORIZE_REVIEW_STATUSES.manual ||
    value.status === INVENTORIZE_REVIEW_STATUSES.parsed ||
    value.status === INVENTORIZE_REVIEW_STATUSES.unsure ||
    value.status === INVENTORIZE_REVIEW_STATUSES.accepted
  );
}

function readBestCandidate(aiExtractions: AiExtraction[], field: OcrCandidateField): OcrCandidate | undefined {
  return readOcrCandidates(aiExtractions, field)[0];
}
